import { Injectable } from "@angular/core";
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { EncryptionServiceService } from "./encryption-service.service";
import { environment } from "src/environment/environment";

@Injectable()
export class EncryptionInterceptorService implements HttpInterceptor {
  constructor(
    private readonly encryptionService: EncryptionServiceService,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    /*
     * Skip-Encryption means the calling service already prepared the request.
     * Remove the internal header before sending it to the API.
     *
     * The response is still checked for EncData/TokenKey and decrypted.
     */
    if (req.headers.has("Skip-Encryption")) {
      const cleanRequest = req.clone({
        headers: req.headers.delete("Skip-Encryption"),
      });

      return next.handle(cleanRequest).pipe(
        map((event: HttpEvent<any>) =>
          this.decryptResponseEvent(event),
        ),
      );
    }

    /*
     * File upload requests cannot be encrypted automatically because a File
     * cannot be JSON-stringified into the encrypted body.
     *
     * The service must manually place:
     *
     * File
     * EncData
     * TokenKey
     *
     * inside FormData.
     *
     * We bypass request encryption here but still decrypt the encrypted API
     * response.
     */
    if (req.body instanceof FormData) {
      return next.handle(req).pipe(
        map((event: HttpEvent<any>) =>
          this.decryptResponseEvent(event),
        ),
      );
    }

    const isHospitalApi = req.url.includes(
      environment.hmApiURL,
    );

    const isSocietyApi = req.url.includes(
      environment.smApiURL,
    );

    const isSchoolApi = req.url.includes(
      environment.speApiURL,
    );

    const isMasterApi = req.url.includes(
      environment.apiURL,
    );

    const isInvestApi = req.url.includes(
      environment.plApiURL + "/PL/SG",
    );

    const isUserBankAccountApi = req.url.includes(
      environment.plApiURL + "/PL/UBA",
    );

    const isPaymentApi = req.url.includes(
      environment.plApiURL + "/PL/PG",
    );
    const isInventoryApi = req.url.includes(
      environment.invApiURL + "/IN",
    );
   const isCrmApi = req.url.includes(
  environment.crmApiURL + '/CRM'
);

  //   const normalizedUrl = req.url.toLowerCase();

  //   const isBBPSApi =
  //     normalizedUrl.includes("/bbps/reh/") ||
  //     normalizedUrl.includes("/bbps/recharge/") ||
  //     normalizedUrl.includes("/bbps/blr/") ||
  //     normalizedUrl.includes("/bbps/biller/") ||
  //     normalizedUrl.includes("/bbps/cc/") ||
  //     normalizedUrl.includes(
  //       "/bbps/creditcardpayment/",
  //     )||
  // normalizedUrl.includes('/bbps/supportticket/');

  //   const isAuthApi =
  //     normalizedUrl.includes("/login") ||
  //     normalizedUrl.includes("/signup") ||
  //     normalizedUrl.includes("/forgot-password") ||
  //     normalizedUrl.includes("/auth/login");

  //   /*
  //    * Requests outside the configured APIs continue without encryption.
  //    */
  //   if (
  //     !isHospitalApi &&
  //     !isSocietyApi &&
  //     !isMasterApi &&
  //     !isSchoolApi &&
  //     !isInvestApi &&
  //     !isPaymentApi &&
  //     !isUserBankAccountApi &&
  //     !isBBPSApi
  //    &&!isBBPSApi) {
  //     return next.handle(req);
  //   }

const normalizedUrl = req.url.toLowerCase();

const isBBPSApi =
  normalizedUrl.includes("/bbps/");

const isAuthApi =
  normalizedUrl.includes("/login") ||
  normalizedUrl.includes("/signup") ||
  normalizedUrl.includes("/forgot-password") ||
  normalizedUrl.includes("/auth/login");

if (
  !isHospitalApi &&
  !isSocietyApi &&
  !isMasterApi &&
  !isSchoolApi &&
  !isInvestApi &&
  !isPaymentApi &&
  !isUserBankAccountApi &&
  !isBBPSApi &&
  !isInventoryApi &&
  !isCrmApi
) {
  return next.handle(req);
}

    /*
     * Authentication, GET and empty-body requests continue unchanged.
     */
    if (
      isAuthApi ||
      req.method === "GET" ||
      !req.body
    ) {
      return next.handle(req);
    }

    const isAlreadyEncrypted =
      req.body?.encData !== undefined ||
      req.body?.EncData !== undefined;

    const hasTokenKey =
      req.body?.tokenKey !== undefined ||
      req.body?.TokenKey !== undefined;

    /*
     * Do not encrypt an already-encrypted body again.
     * Still decrypt its API response.
     */
    if (isAlreadyEncrypted && hasTokenKey) {
      return next.handle(req).pipe(
        map((event: HttpEvent<any>) =>
          this.decryptResponseEvent(event),
        ),
      );
    }

    const encryptedBody =
      this.encryptionService.createEncryptedRequest(
        req.body,
      );

    const modifiedRequest = req.clone({
      body: encryptedBody,
    });

    return next.handle(modifiedRequest).pipe(
      map((event: HttpEvent<any>) =>
        this.decryptResponseEvent(event),
      ),
    );
  }

  /**
   * Decrypt an encrypted API response while leaving normal API responses
   * untouched.
   */
  private decryptResponseEvent(
    event: HttpEvent<any>,
  ): HttpEvent<any> {
    if (!(event instanceof HttpResponse)) {
      return event;
    }

    const body = event.body;

    if (!body) {
      return event;
    }

    const hasEncData =
      body?.encData !== undefined ||
      body?.EncData !== undefined;

    const hasTokenKey =
      body?.tokenKey !== undefined ||
      body?.TokenKey !== undefined;

    if (!hasEncData || !hasTokenKey) {
      return event;
    }

    const decryptedBody =
      this.encryptionService.decryptResponse(body);

    return event.clone({
      body: decryptedBody,
    });
  }
}